import { FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '@/features/workspaces/hooks';
import {
  CreditPricingPayload,
  useCheckCreditValidityMutation,
  useGetCreditBalancesQuery,
} from '@/services/endpoints/credits';
import { CampaignCreditChannel, CREDIT_TYPE_BY_CHANNEL, PACKAGES_ROUTE_BY_CHANNEL } from '../config';
import { formatCreditDateTime, formatCredits } from '../utils/formatCredits';
import { isCreditsFeatureUnavailable } from '../utils/isCreditsFeatureUnavailable';

type CampaignsCreditPreSendCheckProps = {
  channel: CampaignCreditChannel;
  pricing: CreditPricingPayload | null;
  /** ISO-8601 instant for the check (now or scheduled start). */
  at: string;
  scheduleLabel?: string;
  onValidityChange?: (canSend: boolean) => void;
};

export const CampaignsCreditPreSendCheck: FC<CampaignsCreditPreSendCheckProps> = ({
  channel,
  pricing,
  at,
  scheduleLabel,
  onValidityChange,
}) => {
  const { t, i18n } = useTranslation('campaign');
  const workspace = useWorkspace();
  const creditType = CREDIT_TYPE_BY_CHANNEL[channel];

  const { error: balancesError } = useGetCreditBalancesQuery(
    { workspaceId: workspace.id, type: creditType },
    { skip: !workspace.id }
  );

  const [checkValidity, { data, isLoading, isError, error }] = useCheckCreditValidityMutation();
  const creditsUnavailable = isCreditsFeatureUnavailable(balancesError);
  const requestKey = useMemo(
    () => (workspace.id && pricing && at ? JSON.stringify({ workspaceId: workspace.id, type: creditType, pricing, at }) : null),
    [workspace.id, pricing, at, creditType]
  );

  const checkValidityRef = useRef(checkValidity);
  checkValidityRef.current = checkValidity;
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;

  useEffect(() => {
    if (!requestKey || creditsUnavailable) {
      onValidityChangeRef.current?.(true);
      return undefined;
    }

    let cancelled = false;
    onValidityChangeRef.current?.(false);

    const handle = window.setTimeout(() => {
      const request = JSON.parse(requestKey) as {
        workspaceId: string;
        type: string;
        pricing: CreditPricingPayload;
        at: string;
      };

      void checkValidityRef
        .current({
          workspaceId: request.workspaceId,
          type: request.type,
          body: { pricing: request.pricing, at: request.at },
        })
        .unwrap()
        .then((result) => {
          if (cancelled) {
            return;
          }
          onValidityChangeRef.current?.(result.sufficient);
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }
          // 503 kill-switch: treat credits as unavailable and allow send.
          onValidityChangeRef.current?.(isCreditsFeatureUnavailable(err));
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [requestKey, creditsUnavailable]);

  if (isCreditsFeatureUnavailable(balancesError) || isCreditsFeatureUnavailable(error)) {
    return null;
  }

  if (!pricing) {
    return null;
  }

  const sufficient = data?.sufficient;
  const reservable = data?.reservableAtInstant;
  const packagesPath = PACKAGES_ROUTE_BY_CHANNEL[channel];
  const isChecking = isLoading || (!isError && sufficient === undefined);
  const isFailing = sufficient === false;

  const iconClass = isChecking
    ? 'bg-amber-500'
    : isFailing || isError
      ? 'bg-red-500'
      : sufficient
        ? 'bg-emerald-500'
        : 'bg-secondary-400';

  const detail = isChecking
    ? t('credits.preSend.checking')
    : isError
      ? t('credits.preSend.checkError')
      : sufficient
        ? t('credits.preSend.sufficientDetail', {
            amount: formatCredits(reservable ?? 0, i18n.language),
            when: scheduleLabel || t('credits.preSend.now'),
          })
        : t('credits.preSend.insufficientDetail', {
            amount: formatCredits(reservable ?? 0, i18n.language),
            when: scheduleLabel || t('credits.preSend.now'),
          });

  return (
    <section className='rounded-2xl border border-secondary-border bg-white-100 p-5'>
      <p className='text-xs font-extrabold uppercase tracking-[0.05em] text-secondary-600'>{t('credits.preSend.title')}</p>
      <div className='mt-4 flex items-start gap-3'>
        <span className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-extrabold text-white-100 ${iconClass}`}>
          {isChecking ? '!' : isFailing || isError ? '✕' : '✓'}
        </span>
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-bold text-slate-900'>{t('credits.preSend.creditRow')}</p>
          <p className='mt-1 text-sm text-secondary-600'>{detail}</p>
          {isFailing ? (
            <a
              href={packagesPath}
              target='_blank'
              rel='noopener noreferrer'
              className='mt-2 inline-block text-sm font-bold text-brand-700 hover:underline'
            >
              {t('credits.preSend.requestLink')}
            </a>
          ) : null}
        </div>
      </div>
      {scheduleLabel ? (
        <p className='mt-3 text-xs text-secondary-500'>
          {t('credits.preSend.scheduleNote', { date: scheduleLabel })}
        </p>
      ) : null}
      {!scheduleLabel && at ? (
        <p className='mt-3 text-xs text-secondary-500'>
          {t('credits.preSend.instantNote', { date: formatCreditDateTime(at, i18n.language) })}
        </p>
      ) : null}
    </section>
  );
};
